import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "../../../../../controls/buttons/FooterButton";
import { TextFieldSearchFilter } from "../../../../../shared/TextFieldSearchFilter";
import { useOperatorsOptionsFunctions } from "../hooks/useOperatorsOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { CatalogOperator } from "../models/operator";
import { OperatorModal } from "./OperatorModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  getCatalogOperatorsFromAPI: Function;
  isLoading: boolean;
  operatorId: String | undefined;
};

export const TableOperator = ({
  getCatalogOperatorsFromAPI,
  isLoading,
  operatorId,
}: Props) => {
  const navigate = useNavigate();

  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [operator, setOperator] = useState<CatalogOperator | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewOperator = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setOperator(null);
    navigate(`/configuration/operators/new`);
  };

  const { getMenuOptions } = useOperatorsOptionsFunctions({
    setOperator,
    setShowModal,
    handleNewOperator,
  });
  const { rows, columnDefs } = useTableData({
    setOperator,
    setShowModal,
    showContextMenu,
  });
  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: CatalogOperator) => {
      setShowModal("edit");
      setOperator(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setOperator(event.data as CatalogOperator);
      setShowModal("edit");
      navigate(
        `/configuration/operators/${(event.data as CatalogOperator).OperatorId}`,
      );
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(
        `/configuration/operators/${(event.data as CatalogOperator).OperatorId}`,
      );
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "operatorContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setOperator(null);
    navigate(`/configuration/operators/`);
  };

  useEffect(() => {
    if (operatorId) {
      if (operatorId === "new") {
        handleNewOperator();
      } else {
        let partNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if ((n.data as CatalogOperator).OperatorId === Number(operatorId)) {
            partNode = n;
            return true;
          }
        });

        if (partNode!) {
          gridApi?.ensureIndexVisible(partNode.rowIndex ?? 0, "middle");
          partNode.setSelected(true);
          setOperator(partNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "operatorContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Operators
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ margin: "10px 18px" }}>
          <TextFieldSearchFilter
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              gridApi?.setGridOption("quickFilterText", val);
            }}
          />
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh", paddingBottom: "20px" }}>
            <AgGridReact<CatalogOperator>
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={columnSimpleDefaults}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              loading={isLoading}
              onGridReady={(params) => setGridApi(params.api)}
              getRowId={(params) => String(params.data.OperatorId)}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton label="Operator" onClick={handleNewOperator} />
        </Grid>
      </Grid>
      <OperatorModal
        operator={operator}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
        refreshData={getCatalogOperatorsFromAPI}
      />
    </>
  );
};
